import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Layers, Gem, Sparkles, MoreHorizontal, DollarSign, PieChart, TrendingUp, X, MessageSquareQuote, Megaphone } from 'lucide-react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import type { TabId } from '../../types';

const PRIMARY_TABS: { id: TabId | 'chat'; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'summary',  label: 'Market',   icon: LayoutDashboard },
  { id: 'builder',  label: 'Packages', icon: Layers },
  { id: 'featureValue', label: 'Features', icon: Gem },
  { id: 'chat',     label: 'AI Chat',  icon: Sparkles },
];

const MORE_TABS: { id: TabId; label: string; icon: typeof DollarSign }[] = [
  { id: 'price',      label: 'Pricing',    icon: DollarSign },
  { id: 'verbatims',  label: 'Voice',      icon: MessageSquareQuote },
  { id: 'segments',   label: 'Segments',   icon: PieChart },
  { id: 'financial',  label: 'Financials', icon: TrendingUp },
  { id: 'marketing',  label: 'Marketing',  icon: Megaphone },
];

export function MobileNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const activeTab = useSimulatorStore(s => s.activeTab);
  const setActiveTab = useSimulatorStore(s => s.setActiveTab);
  const toggleChat = useSimulatorStore(s => s.toggleChatPanel);
  const setChatPanelOpen = useSimulatorStore(s => s.setChatPanelOpen);
  const setLastTabBeforeChat = useSimulatorStore(s => s.setLastTabBeforeChat);
  const chatOpen = useSimulatorStore(s => s.chatPanelOpen);

  const handleTab = (id: TabId | 'chat') => {
    if (id === 'chat') {
      setLastTabBeforeChat(activeTab);
      toggleChat();
    } else {
      setChatPanelOpen(false);
      setActiveTab(id);
    }
    setMoreOpen(false);
  };

  const isMoreActive = MORE_TABS.some(t => t.id === activeTab);

  return (
    <>
      <AnimatePresence>
        {moreOpen && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-end justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
            <motion.div
              className="relative w-full rounded-t-2xl shadow-2xl"
              style={{ backgroundColor: 'var(--color-card)', borderTop: '1px solid var(--color-border)' }}
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <span className="text-sm font-semibold text-primary">More Tabs</span>
                <button onClick={() => setMoreOpen(false)} className="p-1 text-muted hover:text-primary">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 px-4 pb-6">
                {MORE_TABS.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTab(tab.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-chart-teal/15 text-chart-teal'
                          : 'text-secondary hover:text-primary hover:bg-surface'
                      }`}
                    >
                      <Icon size={18} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <nav
        className="fixed bottom-0 left-0 right-0 z-[60] md:hidden border-t"
        style={{
          backgroundColor: 'var(--color-card)',
          borderColor: 'var(--color-border)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex items-center justify-around px-1 pt-1 pb-1">
          {PRIMARY_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = tab.id === 'chat' ? chatOpen : activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTab(tab.id)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all min-w-[56px] ${
                  isActive
                    ? 'text-chart-teal'
                    : 'text-muted hover:text-primary'
                }`}
              >
                <Icon size={20} />
                <span className="text-micro font-medium leading-tight">{tab.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all min-w-[56px] ${
              isMoreActive ? 'text-chart-teal' : 'text-muted hover:text-primary'
            }`}
          >
            <MoreHorizontal size={20} />
            <span className="text-micro font-medium leading-tight">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
